/**
 * features/finance/studentDiscountWrite.ts
 * ------------------------------------------------------------------
 * Recording and ending a per-pupil concession.
 *
 * THE SCHOOL'S ORDER OF WORK. The billing run raises every invoice at the
 * FULL fee; the bursar discounts afterwards, pupil by pupil. So a concession
 * here is a REGISTER ENTRY — who gets what, and why — and applying it to a
 * bill is a separate, deliberate act.
 *
 * That makes every application a CREDIT NOTE, not a discount line. The
 * invoice posted at its full amount when it was raised; reducing it now is a
 * second event needing its own number, its own reason and its own journal.
 * So this module never edits an invoice — it calls createAdjustment, the
 * audited path that already exists.
 *
 * IT POSTS TO CONTRA-REVENUE, not against tuition. A credit note defaults to
 * reversing out of the income account it came from; a concession is stamped
 * to 4900 Discounts & Bursaries Granted instead. The difference matters at
 * year end: reversing tuition understates what the school billed and hides
 * what it gave away, while 4900 keeps gross fee income intact and shows the
 * concessions as the deduction from revenue they are.
 *
 * APPLYING TWICE IS THE REAL HAZARD of doing this by hand. A 10% sibling
 * discount credited twice against one invoice is money out of the school
 * with two credit notes and nothing to say it was one concession. Every
 * application is recorded on the concession and the second is refused.
 *
 * A discount is never DELETED, only ended. What the school gave up, and
 * when it stopped, is the record.
 * ------------------------------------------------------------------ */
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { db, setDoc, updateDoc } from '@/lib/firebase';
import {
  FINANCE_COLLECTIONS, addDiscountToInvoice, lineCategoryTotal, round2, type Invoice,
} from '@/lib/financeCore';
import { GL_COLLECTIONS, journalIdFor } from '@/lib/gl';
import {
  validateDiscount, defaultLabel, toEngineDiscount, alreadyApplied,
  discountRoute, toInvoiceDiscount,
  type StudentDiscount, type DiscountMode, type AppliedCredit, type RouteDecision,
  type DiscountRoute,
} from '@/lib/studentDiscounts';
import { adjustableRemaining } from '@/lib/adjustments';
import { applyManualDiscounts } from '@/lib/financeCore';
import { createAdjustment } from '@/features/finance/adjustmentWrite';
import { auditFromContext } from '@/lib/audit';

const COL = FINANCE_COLLECTIONS.studentDiscounts;

export interface Actor {
  uid?: string;
  name?: string;
  email?: string;
  displayName?: string;
}

export interface NewDiscountInput {
  studentId: string;
  studentName?: string;
  kind: StudentDiscount['kind'];
  mode: DiscountMode;
  value: number;
  appliesToCategory: StudentDiscount['appliesToCategory'];
  reason: string;
  actor: Actor;
}

/** Every discount ever recorded against a pupil, newest first. */
export async function fetchStudentDiscounts(studentId: string): Promise<StudentDiscount[]> {
  const snap = await getDocs(query(collection(db, COL), where('studentId', '==', studentId)));
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }) as StudentDiscount)
    .sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)));
}

export async function createStudentDiscount(input: NewDiscountInput): Promise<StudentDiscount> {
  const bad = validateDiscount(input);
  if (bad) throw new Error(bad);

  const ref = doc(collection(db, COL));
  const at = new Date().toISOString();
  const record: StudentDiscount = {
    id: ref.id,
    studentId: input.studentId,
    ...(input.studentName ? { studentName: input.studentName } : {}),
    kind: input.kind,
    mode: input.mode,
    value: input.mode === 'amount' ? round2(input.value) : input.value,
    appliesToCategory: input.appliesToCategory,
    reason: input.reason.trim(),
    active: true,
    createdAt: at,
    createdBy: input.actor.uid,
    ...(input.actor.name ? { createdByName: input.actor.name } : {}),
  };

  await setDoc(ref, record);

  auditFromContext(input.actor as never, 'student_discount_created', 'finance', {
    targetId: input.studentId,
    targetType: 'student',
    targetName: input.studentName,
    details: {
      kind: record.kind,
      mode: record.mode,
      value: record.value,
      appliesTo: record.appliesToCategory,
      reason: record.reason,
    },
  });

  return record;
}

/**
 * Stop a discount applying to future invoices.
 *
 * Deliberately not a delete. Invoices already raised under it keep their
 * discount line and the journals that posted them stand; what changes is
 * only what happens next time. Removing the record would leave those
 * invoices carrying a reduction with nothing on file explaining it.
 */
export async function endStudentDiscount(
  d: StudentDiscount,
  reason: string,
  actor: Actor,
): Promise<void> {
  const why = String(reason ?? '').trim();
  if (why.length < 3) throw new Error('Give a reason for ending it.');
  const at = new Date().toISOString();

  await updateDoc(doc(db, COL, d.id), {
    active: false,
    endedAt: at,
    endedBy: actor.uid ?? '',
    endedReason: why,
  });

  auditFromContext(actor as never, 'student_discount_ended', 'finance', {
    targetId: d.studentId,
    targetType: 'student',
    targetName: d.studentName,
    details: { kind: d.kind, mode: d.mode, value: d.value, reason: why },
  });
}

/**
 * What this concession WOULD take off an invoice already issued, and why it
 * cannot when it cannot.
 *
 * Shown before the bursar commits, because a percentage of a bill is not a
 * figure anyone does in their head, and the figure IS the money the school
 * gives up.
 *
 * TWO SHAPES OF INVOICE, and the second is most of them.
 *
 *   A BILLED invoice carries `lines`, so a concession scoped to tuition can
 *   find the tuition and take its share.
 *
 *   AN OPENING BALANCE does not. 629 of the invoices on this system are
 *   `OPEN/2026/…` brought over from Pastel: a total, a balance, and no
 *   breakdown of what the money was for. A tuition-scoped concession has
 *   nothing to bite on and comes to zero — not because the school owes
 *   nothing, but because nobody recorded what the debt was made of. So a
 *   whole-bill concession falls back to the OUTSTANDING BALANCE, and a
 *   category-scoped one says plainly that it cannot be worked out rather
 *   than silently reporting nil.
 *
 * CAPPED AT WHAT IS OUTSTANDING either way. createAdjustment refuses to
 * credit more than remains; the preview has to agree with it or the bursar
 * is shown a figure the save will reject.
 */
export interface DiscountPreview {
  amount: number;
  /** Why it comes to nothing, when it does. Null when the amount stands. */
  blockedReason: string | null;
}

export function previewDetail(
  d: StudentDiscount,
  invoice: Invoice,
  alreadyAdjusted = Number(invoice.adjusted) || 0,
): DiscountPreview {
  const outstanding = adjustableRemaining(
    { total: Number(invoice.total) || 0, paid: Number(invoice.paid) || 0 },
    alreadyAdjusted,
  );
  if (outstanding <= 0) {
    return { amount: 0, blockedReason: 'Nothing is outstanding on this invoice.' };
  }

  const lines = invoice.lines ?? [];
  const hasBreakdown = lines.length > 0;

  /* CONCESSIONS ADD, AND STOP AT THE CHARGE.
   *
   * Fee Special Cases reduces a bill WHEN IT IS RAISED — a bursary or a
   * staff remission comes off inside the billing run and lands as a discount
   * line on the invoice. This screen credits a bill AFTER it is raised. Two
   * routes, one tuition charge, and neither used to know about the other: a
   * 60% bursary in the run plus a 50% sibling percentage on the gross came
   * to 110% of a fee the school charged once.
   *
   * Both are percentages OF THE FULL FEE — that is what the school means by
   * a 50% sibling discount — so they add rather than compound, and the sum
   * is capped at the charge itself. `room` is what is left of the category
   * after the discounts already granted against it; a concession may reach
   * that and no further. A pupil can end up paying nothing. A pupil can
   * never be given back more than they were billed. */
  const alreadyGranted = (cat: string) =>
    round2((invoice.discounts ?? [])
      .filter((x) => {
        // A discount scoped to the WHOLE bill has already eaten into every
        // category, so it competes with a category-scoped one. And a line
        // raised before Sep 2026 carries no scope at all — read as 'all',
        // which is the cautious answer: it grants less, never more.
        const scope = x.appliesToCategory ?? 'all';
        return scope === 'all' || cat === 'all' || scope === cat;
      })
      .reduce((a, x) => a + (Number(x.amount) || 0), 0));

  let raw: number;
  if (hasBreakdown) {
    const granted = alreadyGranted(String(d.appliesToCategory));
    const gross = round2(
      applyManualDiscounts([toEngineDiscount(d)], lines).reduce((a, o) => a + o.amount, 0),
    );
    const base = lineCategoryTotal(lines, d.appliesToCategory);
    const room = Math.max(0, round2(base - granted));
    raw = round2(Math.min(gross, room));
    if (granted > 0 && raw <= 0) {
      return {
        amount: 0,
        blockedReason:
          'That charge has already been fully discounted on the invoice — there is nothing left '
          + 'to take a sibling discount from.',
      };
    }
    if (raw <= 0) {
      return {
        amount: 0,
        blockedReason: d.appliesToCategory === 'all'
          ? 'This concession comes to nothing against that invoice.'
          : `That invoice carries no ${String(d.appliesToCategory)} charge.`,
      };
    }
  } else if (d.appliesToCategory !== 'all') {
    // An opening balance with a category-scoped concession. Refusing with a
    // reason beats reporting nil: the bursar can record a whole-bill
    // concession instead, which is a decision, not a workaround.
    return {
      amount: 0,
      blockedReason:
        'That invoice is a balance brought forward and has no fee breakdown, '
        + `so a ${String(d.appliesToCategory)}-only concession cannot be worked out. `
        + 'Record one that applies to the whole bill.',
    };
  } else {
    raw = d.mode === 'percent'
      ? round2((outstanding * Math.min(100, Math.max(0, d.value))) / 100)
      : round2(Math.max(0, d.value));
  }

  const amount = round2(Math.min(raw, outstanding));
  return amount > 0
    ? { amount, blockedReason: null }
    : { amount: 0, blockedReason: 'This concession comes to nothing against that invoice.' };
}

/** Just the figure. */
export function previewAgainstInvoice(d: StudentDiscount, invoice: Invoice): number {
  return previewDetail(d, invoice).amount;
}

/**
 * Record a once-off concession AND take it off a bill, in one act.
 *
 * THESE WERE TWO STEPS AND SHOULD NEVER HAVE BEEN. Recording a discount
 * looked complete — a row appeared, with a pupil, an amount and a reason —
 * while nothing whatever had happened to a bill. On 5 September the school
 * found three concessions worth P4,000 sitting in that state: the bursar
 * had done what the screen asked and the parents still owed the full fee.
 *
 * A sibling discount here is a single reduction against a single bill. It
 * has no future: it is not a standing arrangement, and there is no next
 * invoice for it to apply itself to. So recording it and spending it are
 * the same action, and the record is closed the moment the credit note
 * exists.
 *
 * THE RECORD IS WRITTEN FIRST, DELIBERATELY. If the credit note fails, a
 * recorded-but-unapplied concession is left behind — visible on the screen,
 * flagged, and re-appliable. The other order would raise a credit note with
 * no concession on file explaining it, which is money off a bill with no
 * reason attached.
 */
export async function recordAndApply(
  input: NewDiscountInput,
  invoice: Invoice,
  actor: Actor,
): Promise<{ discount: StudentDiscount; amount: number; adjustmentNo: string; route: DiscountRoute }> {
  const preview = previewDetail(
    { ...input, id: 'preview', active: true, createdAt: '' } as StudentDiscount,
    invoice,
  );
  if (preview.amount <= 0) {
    // Refused BEFORE anything is written. A concession that comes to
    // nothing should not leave a record claiming the school gave something.
    throw new Error(preview.blockedReason || 'This concession comes to nothing against that invoice.');
  }

  /* THE SCHOOL ASKED FOR A DISCOUNT, SO GIVE THEM A DISCOUNT. Which
     instrument is available is a question about the ledger, not about
     preference — see discountRoute. Today nothing has posted, so this is
     a discount on the bill; after the GL cut-over the same call routes
     itself to a credit note with nothing to remember to change. */
  const { route } = await routeFor(invoice);

  const discount = await createStudentDiscount(input);
  const applied = route === 'discount'
    ? { ...await discountIssuedInvoice(discount, invoice, actor), adjustmentNo: '' }
    : await creditIssuedInvoice(discount, invoice, actor);

  // Spent. Not "ended" — nothing was cancelled; it did exactly what it was
  // recorded to do, and there is no future for it to apply to.
  await updateDoc(doc(db, COL, discount.id), { active: false });

  return {
    discount: { ...discount, active: false },
    amount: applied.amount,
    adjustmentNo: applied.adjustmentNo,
    route,
  };
}


/**
 * Has this invoice posted to the ledger?
 *
 * Journal ids are deterministic, so this is one read of a known document
 * rather than a query. FAILS SAFE: if the read is refused — a bursar
 * without ledger access, a rule change — it answers "posted", which routes
 * to a credit note. Wrongly raising a credit note is a tidy document the
 * school did not need; wrongly editing an invoice behind a real journal
 * puts the sub-ledger and the GL out of agreement.
 */
export async function invoiceHasPosted(invoiceId: string): Promise<boolean> {
  try {
    const snap = await getDoc(doc(db, GL_COLLECTIONS.journals, journalIdFor('invoice', invoiceId)));
    return snap.exists();
  } catch {
    return true;
  }
}

/** What will happen if the bursar applies this now, and why. */
export async function routeFor(invoice: Invoice): Promise<RouteDecision> {
  return discountRoute(await invoiceHasPosted(invoice.id));
}

/**
 * Apply a concession as a DISCOUNT ON THE INVOICE — the school's own
 * meaning of the words "sibling discount".
 *
 * Only ever reached for an invoice that has not posted; `routeFor` decides,
 * and this refuses on its own account rather than trusting the caller,
 * because the one thing that must never happen is an invoice being reduced
 * behind a journal that says otherwise.
 */
export async function discountIssuedInvoice(
  d: StudentDiscount,
  invoice: Invoice,
  actor: Actor,
): Promise<{ amount: number }> {
  if (await invoiceHasPosted(invoice.id)) {
    throw new Error(
      'That invoice has already posted to the ledger, so it cannot be edited. '
      + 'Raise it as a credit note instead.',
    );
  }

  const done = alreadyApplied(d, invoice.id);
  if (done) {
    throw new Error(
      `Already applied to ${invoice.invoiceNo || 'this invoice'} on ${String(done.at).slice(0, 10)}.`,
    );
  }

  const { amount, blockedReason } = previewDetail(d, invoice);
  if (amount <= 0) {
    throw new Error(blockedReason || 'This concession comes to nothing against that invoice.');
  }

  const next = addDiscountToInvoice(invoice, toInvoiceDiscount(d, amount));
  await updateDoc(doc(db, FINANCE_COLLECTIONS.invoices, invoice.id), {
    ...next,
    updatedAt: new Date().toISOString(),
  });

  /* Recorded on the concession in the same shape a credit note leaves, so
     `alreadyApplied` refuses a second helping whichever route was taken. */
  const applied: AppliedCredit = {
    invoiceId: invoice.id,
    ...(invoice.invoiceNo ? { invoiceNo: invoice.invoiceNo } : {}),
    adjustmentId: '',
    amount,
    at: new Date().toISOString(),
    ...(actor.uid ? { by: actor.uid } : {}),
  };
  await updateDoc(doc(db, COL, d.id), { appliedTo: [...(d.appliedTo ?? []), applied] });

  auditFromContext(actor as never, 'student_discount_applied', 'finance', {
    targetId: d.studentId,
    targetType: 'student',
    targetName: d.studentName,
    details: {
      invoiceNo: invoice.invoiceNo, amount, as: 'discount',
      kind: d.kind, reason: d.reason,
    },
  });

  return { amount };
}

/**
 * Apply a recorded concession to one invoice, by whichever instrument the
 * ledger allows. The single entry point the screen should use — so the
 * decision is made in one place rather than by whoever calls what.
 */
export async function applyToInvoice(
  d: StudentDiscount,
  invoice: Invoice,
  actor: Actor,
): Promise<{ amount: number; adjustmentNo: string; route: DiscountRoute }> {
  const { route } = await routeFor(invoice);
  if (route === 'discount') {
    const { amount } = await discountIssuedInvoice(d, invoice, actor);
    return { amount, adjustmentNo: '', route };
  }
  const r = await creditIssuedInvoice(d, invoice, actor);
  return { ...r, route };
}

/**
 * Apply a discount to an invoice that has ALREADY been raised, as a credit
 * note through the existing audited path.
 *
 * Not a discount line and not an edit to the invoice: the invoice posted at
 * its full amount when it was issued, so the reduction is its own event with
 * its own number and its own journal.
 */
export async function creditIssuedInvoice(
  d: StudentDiscount,
  invoice: Invoice,
  actor: Actor,
): Promise<{ amount: number; adjustmentNo: string }> {
  /* Refuse a second credit for the same concession on the same invoice.
     Checked against the record rather than by scanning credit notes: a
     legitimate second credit note for a DIFFERENT reason must still be
     possible on the same invoice. */
  const done = alreadyApplied(d, invoice.id);
  if (done) {
    throw new Error(
      `Already credited against ${invoice.invoiceNo || 'this invoice'} `
      + `on ${String(done.at).slice(0, 10)}${done.adjustmentNo ? ` (${done.adjustmentNo})` : ''}.`,
    );
  }

  const { amount, blockedReason } = previewDetail(d, invoice);
  if (amount <= 0) {
    throw new Error(blockedReason || 'This concession comes to nothing against that invoice.');
  }

  const adj = await createAdjustment({
    kind: 'credit_note',
    invoice: { id: invoice.id },
    amount,
    reason: `${defaultLabel(toEngineDiscount(d))} — ${d.reason}`,
    date: new Date().toISOString().slice(0, 10),
    actor,
    /* Contra-revenue, not a reversal of tuition. See the header. Resolved by
       SYSTEM KEY so it survives the school renumbering its chart. */
    incomeAccountKey: 'DISCOUNTS_GRANTED',
  });

  const credit: AppliedCredit = {
    invoiceId: invoice.id,
    ...(invoice.invoiceNo ? { invoiceNo: invoice.invoiceNo } : {}),
    adjustmentId: adj.id,
    adjustmentNo: adj.adjustmentNo,
    amount,
    at: new Date().toISOString(),
    ...(actor.uid ? { by: actor.uid } : {}),
  };
  await updateDoc(doc(db, COL, d.id), {
    appliedTo: [...(d.appliedTo ?? []), credit],
  });

  return { amount, adjustmentNo: adj.adjustmentNo };
}
